import { supabase } from './supabase';
import { ErrorDeApi } from './error-api';

/**
 * El rastro de los fallos del cliente: van a `auditoria_eventos`, que lee la Auditoría del Panel.
 * Si algún día entra un servicio de errores, este es el único módulo que cambia. El
 * `correlation_id` es el `uuid_cliente` de la venta: un fallo se sigue hasta su comprobante.
 */

/** Nunca se envía dos veces el mismo fallo: un bucle de render no debe inundar la auditoría. */
const yaEnviados = new Set<string>();
const TOPE_POR_SESION = 20;

function huella(datos: { codigo?: string; mensaje: string; pantalla: string }): string {
  return `${datos.codigo ?? ''}|${datos.mensaje}|${datos.pantalla}`;
}

/** Campos de texto que traen los errores de la capa de datos, en el orden en que informan. */
const CAMPOS = ['message', 'details', 'hint', 'error_description', 'error'] as const;

/**
 * El texto y el código de un fallo, sea lo que sea lo que llegó: un `Error` trae `message`, el de
 * PostgREST es plano, y a `unhandledrejection` llega cualquier cosa. La huella lo necesita distinto.
 */
export function describirError(error: unknown): { mensaje: string; codigo?: string } {
  if (error instanceof ErrorDeApi) {
    return { mensaje: error.message, codigo: error.codigoDominio ?? error.codigo };
  }
  if (error instanceof Error) return { mensaje: error.message || error.name };
  if (typeof error === 'object' && error !== null) {
    const o = error as Record<string, unknown>;
    const codigo = typeof o.code === 'string' ? o.code : undefined;
    const partes = CAMPOS.map((c) => (typeof o[c] === 'string' && o[c] ? (o[c] as string) : null)).filter(Boolean);
    if (partes.length > 0) return { mensaje: partes.join(' · '), codigo };
    // Sin ningún campo conocido se guarda la forma: saber qué llegó vale más que «[object Object]».
    try {
      return { mensaje: JSON.stringify(o), codigo };
    } catch {
      // Referencias circulares: al menos las claves.
      return { mensaje: `objeto sin mensaje, claves: ${Object.keys(o).join(', ')}`, codigo };
    }
  }
  return { mensaje: String(error) };
}

/**
 * Deja constancia de un fallo. Nunca lanza: si el rastro falla, el problema original ya es bastante.
 * `correlacion` es el `uuid_cliente` de la venta cuando el fallo viene del mostrador.
 */
export async function rastrearFallo(
  error: unknown,
  contexto: { pantalla: string; correlacion?: string } = { pantalla: 'desconocida' }
): Promise<void> {
  try {
    const esApi = error instanceof ErrorDeApi;
    const { mensaje, codigo } = describirError(error);
    const datos = {
      codigo,
      mensaje: mensaje.slice(0, 300),
      pantalla: contexto.pantalla,
    };

    const clave = huella(datos);
    if (yaEnviados.has(clave) || yaEnviados.size >= TOPE_POR_SESION) return;
    yaEnviados.add(clave);

    // Los logs estructurados del navegador llevan el mismo identificador que la fila de `ventas`,
    // así que una venta se puede seguir de la pantalla a la base.
    console.error(
      JSON.stringify({
        nivel: 'error',
        correlation_id: contexto.correlacion ?? null,
        pantalla: datos.pantalla,
        codigo: datos.codigo ?? null,
        status: esApi ? error.status : null,
        mensaje: datos.mensaje,
      })
    );

    await supabase.rpc('registrar_fallo_cliente', {
      p_pantalla: datos.pantalla,
      p_codigo: datos.codigo ?? null,
      p_mensaje: datos.mensaje,
      p_correlacion: contexto.correlacion ?? null,
      p_status: esApi ? (error.status ?? null) : null,
    });
  } catch {
    // Sin red o sin sesión no hay nada que hacer: el fallo original ya se le mostró a la persona.
  }
}

/**
 * Engancha lo que nadie captura: una excepción que se escapa y una promesa rechazada sin `catch`.
 * Son justo los fallos que hoy no dejan rastro en ninguna parte.
 */
export function iniciarRastro(): void {
  window.addEventListener('error', (e) => {
    void rastrearFallo(e.error ?? e.message, { pantalla: location.pathname });
  });
  window.addEventListener('unhandledrejection', (e) => {
    void rastrearFallo(e.reason, { pantalla: location.pathname });
  });
}
